import React, { useCallback, useMemo, useState } from "react";
import { useMutation } from "urql";

import {
  formatPresentation,
  getProductData,
  getSelectedItems,
  handleMultipleFilesDownload,
  handleSingleFileDownload,
} from "utils";
import { fetchData, useDownloadModalCheckboxes, useTranslate } from "hooks";
import { useAppBaseContext } from "providers";
import {
  createLCAProductReportMut,
  createProductMut,
  createProductReport,
  createProductXBIMReport,
} from "api/mutations";
import { useProjectDataStore } from "stores/projectStore";
import { getLCAProductReportQry } from "api/queries";
import { useAuthStore } from "stores/authStore";

import { toast } from "react-toastify";
import { Button } from "./Button";
import { Icon } from "./Icon/Icon";

export const ProductReportsDownloadButtonsBlock = ({
  itemsData,
  setItemsData,
  documents,
  orderCode,
  productDataFromProductConfigurator,
  requestParams,
  setExpandedAccordionIds,
  nodeId,
}) => {
  const translate = useTranslate();
  const [reportsInProgress, setReportsInProgress] = useState(false);
  const { lang } = useAppBaseContext();
  const currentUserTenant = useAuthStore.use.currentUser()?.tenant;

  const units =
    useProjectDataStore((state) => state.units) ||
    requestParams?.resultUnitsSystem ||
    "de_m3ph";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const exeGetLCAProductReport = (variables) =>
    new Promise((resolve, reject) => {
      fetchData({
        query: getLCAProductReportQry,
        variables,
        onSuccess: ({ data }) => {
          resolve(data);
        },
        onError: (err) => {
          err?.message &&
            toast.error(err.originalError.message, {
              type: "error",
              theme: "colored",
            });
          setExpandedAccordionIds(false);
          reject(err);
        },
      });
    });

  const [, exeCreateProduct] = useMutation(createProductMut);
  const [, executeCreateProductReport] = useMutation(createProductReport);
  const [, executeCreateProductXBIMReport] = useMutation(
    createProductXBIMReport,
  );
  const [, exeCreateLCAProductReport] = useMutation(createLCAProductReportMut);

  const { handleSelectAll, handleClearAll } = useDownloadModalCheckboxes({
    setData: setItemsData,
    setExpandedAccordionIds,
    itemsData,
  });
  const resetCheckedItems = itemsData.map((item) => ({
    ...item,
    checked: false,
    checkboxContent: item.checkboxContent
      ? item.checkboxContent.map((subItem) => ({
          ...subItem,
          checked: false,
        }))
      : item.checkboxContent,
  }));

  const handleSubmit = useCallback(async () => {
    setReportsInProgress(true);

    const productData =
      productDataFromProductConfigurator ||
      (await getProductData(
        exeCreateProduct,
        orderCode,
        lang,
        currentUserTenant,
      ));
    const formattedPresentation = formatPresentation(
      productData.product.orderCode.presentation,
    );
    const selectedItems = getSelectedItems(itemsData);
    if (selectedItems.length === 1) {
      await handleSingleFileDownload({
        item: selectedItems[0],
        productData,
        presentation: formattedPresentation,
        units,
        lang,
        orderCode,
        executeCreateProductXBIMReport,
        executeCreateProductReport,
        executeCreateLCAProductReport: exeCreateLCAProductReport,
        executeGetLCAProductReport: exeGetLCAProductReport,
        documents,
        requestParams,
        translate,
        nodeId,
      });
    } else {
      await handleMultipleFilesDownload({
        selectedItems,
        productData,
        presentation: formattedPresentation,
        units,
        lang,
        orderCode,
        executeCreateProductXBIMReport,
        executeCreateProductReport,
        executeCreateLCAProductReport: exeCreateLCAProductReport,
        executeGetLCAProductReport: exeGetLCAProductReport,
        documents,
        requestParams,
        translate,
        nodeId,
      });
    }
    setReportsInProgress(false);
    setItemsData(resetCheckedItems);
  }, [
    productDataFromProductConfigurator,
    exeCreateProduct,
    orderCode,
    lang,
    currentUserTenant,
    itemsData,
    setItemsData,
    resetCheckedItems,
    units,
    executeCreateProductXBIMReport,
    executeCreateProductReport,
    exeCreateLCAProductReport,
    exeGetLCAProductReport,
    documents,
    requestParams,
    translate,
    nodeId,
  ]);

  const checked = useMemo(
    () =>
      itemsData.reduce((acc, item) => {
        if (item.checked) {
          acc.push(item);
        }

        if (item.checkboxContent) {
          const checkedSublistItems = item.checkboxContent.filter(
            (subItem) => subItem.checked,
          );
          acc.push(...checkedSublistItems);
        }

        return acc;
      }, []),
    [itemsData],
  );

  return (
    <div className="d-flex justify-content-end">
      <Button
        data-testid="DownloadModal_select-all-btn"
        onClick={handleSelectAll}
        className="me-3"
        color="secondary"
        outline
      >
        {translate("SELECT_ALL")}
      </Button>
      <Button
        data-testid="DownloadModal_clear-selection-btn"
        onClick={handleClearAll}
        className="me-3"
        color="secondary"
        outline
        disabled={!checked.length}
      >
        {translate("CLEAR_SELECTION")}
      </Button>
      <Button
        data-testid="DownloadModal_download-btn"
        className="d-inline-block text-white"
        onClick={handleSubmit}
        color="primary"
        disabled={checked.length === 0 || reportsInProgress}
        isLoading={reportsInProgress}
      >
        <Icon
          prefix="trox"
          iconName="arrow-down-bracket-regular"
          className="me-1"
        />
        {translate("DOWNLOAD_DATA")}
      </Button>
    </div>
  );
};
